
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import toast from 'react-hot-toast';
import { CommonContext } from '../../../App';
import api from '../../../services/api';
import Navbar from '../../../Components/Navbar/Navbar';
import LoadingSpinner from '../../../Components/CommonComponent/loadingSpiner/LoadingSpinner';
import ContactMarquee from '../../../Components/CommonComponent/Table/ContactMarquee';
import Pagination, { usePagination } from '../../../Components/CommonComponent/Pgination/pagination';
import { SlReload } from "react-icons/sl";
import RecoverDeleteDataModal from '../../../Components/CommonComponent/Modals/RecoverDeleteDataModal';

export default function DeletedEntryData() {
  const [receiptData, setReceiptData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [journalData, setJournalData] = useState([]);
  const [isDateLoading, setIsDateLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(null);
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const { userData, branchId, dateRange, bank, cash } = useContext(CommonContext);
  const adminId = userData?.user?.id;
  const userRole = userData?.user?.role;
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteEntry, setSelectedDeleteEntry] = useState(null);
  const [defaultDateRange, setDefaultDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const {
    currentPage,
    itemsPerPage,
    paymentItemsPerPage,
    paymentCurrentPage,
    salesCurrentPage,
    salesItemsPerPage,
    purchaseItemsPerPage,
    purchaseCurrentPage,
    journalCurrentPage,
    journalItemsPerPage,

    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(10);

  const [receiptTotal, setReceiptTotal] = useState(0);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [salesTotal, setSalesTotal] = useState(0);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [journalTotal, setJournalTotal] = useState(0);
  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);
  useEffect(() => {
    setIsDateLoading(true);
  }, [startDate, endDate, branchId]);

  const fetchReceiptData = useCallback(async (source = "date") => {
    try {
      source === "pagination"
        ? setLoadingTable("receipt")
        : setIsDateLoading(true);

      const res = await api.get(
        `receipt/get-receipt-delete-entry?branchId=${branchId}&date=${startDate}&page=${currentPage}&limit=${itemsPerPage}`
      );

      if (res.data.success) {
        setReceiptData(res.data.entries || []);
        setReceiptTotal(res.data.total);
      }
    } catch (error) {
      toast.error("Failed to load receipt data.");
    } finally {
      setIsDateLoading(false);
      setLoadingTable(null);
    }
  }, [branchId, startDate, currentPage, itemsPerPage, refreshKey]);


  const fetchPaymentData = useCallback(async (source = "date") => {
    try {
      source === "pagination"
        ? setLoadingTable("payment")
        : setIsDateLoading(true);

      const res = await api.get(
        `payment/get-payment-delete-entry?branchId=${branchId}&date=${startDate}&page=${paymentCurrentPage}&limit=${paymentItemsPerPage}`
      );

      if (res.data.success) {
        setPaymentData(res.data.entries || []);
        setPaymentTotal(res.data.total);
      }
    } catch (error) {
      toast.error("Failed to load payment data.");
    } finally {
      setIsDateLoading(false);
      setLoadingTable(null);
    }
  }, [branchId, startDate, paymentCurrentPage, paymentItemsPerPage, refreshKey]);


  const fetchSalesData = useCallback(async (source = "date") => {
    try {
      source === "pagination"
        ? setLoadingTable("sales")
        : setIsDateLoading(true);
      const res = await api.get(`sales/get-sales-delete-entry?branchId=${branchId}&date=${startDate}&page=${salesCurrentPage}&limit=${salesItemsPerPage}`);
      if (res.data.success) {
        setSalesData(res.data.entries || []);
        setSalesTotal(res.data.total);
      }
    } catch (error) {
      toast.error("Failed to load consolidated data.");
      console.error(error);
    } finally {
      setIsDateLoading(false);
      setLoadingTable(null);
    }
  }, [branchId, startDate, salesCurrentPage, salesItemsPerPage, refreshKey]);

  const fetchPurchaseData = useCallback(async (source = "date") => {
    try {
      source === "pagination"
        ? setLoadingTable("purchase")
        : setIsDateLoading(true);
      const res = await api.get(`purchase/get-purchase-delete-entry?branchId=${branchId}&date=${startDate}&page=${purchaseCurrentPage}&limit=${purchaseItemsPerPage}`);
      if (res.data.success) {
        setPurchaseData(res.data.entries || []);
        setPurchaseTotal(res.data.total);
      }
    } catch (error) {
      toast.error("Failed to load consolidated data.");
      console.error(error);
    } finally {
      setIsDateLoading(false);
      setLoadingTable(null);
    }
  }, [branchId, startDate, purchaseCurrentPage, purchaseItemsPerPage, refreshKey]);

  const fetchJournalData = useCallback(async (source = "date") => {
    try {
      source === "pagination"
        ? setLoadingTable("journal")
        : setIsDateLoading(true);
      const res = await api.get(`journal/get-journal-delete-entry?branchId=${branchId}&date=${startDate}&page=${journalCurrentPage}&limit=${journalItemsPerPage}`);
      if (res.data.success) {
        setJournalData(res.data.entries || []);
        setJournalTotal(res.data.total);
      }
    } catch (error) {
      toast.error("Failed to load consolidated data.");
      console.error(error);
    } finally {
      setIsDateLoading(false);
      setLoadingTable(null);
    }
  }, [branchId, startDate, journalCurrentPage, journalItemsPerPage, refreshKey]);

  useEffect(() => {
    if (branchId && startDate) {
      fetchReceiptData();
    }
  }, [fetchReceiptData]);

  useEffect(() => {
    if (branchId && startDate) {
      fetchPaymentData();
    }
  }, [fetchPaymentData]);

  useEffect(() => {
    if (branchId && startDate) {
      fetchSalesData();
    }
  }, [fetchSalesData]);

  useEffect(() => {
    if (branchId && startDate) {
      fetchPurchaseData();
    }
  }, [fetchPurchaseData]);

  useEffect(() => {
    if (branchId && startDate) {
      fetchJournalData();
    }
  }, [fetchJournalData]);


  const hasSales = salesData.some(entry => entry.sales?.length > 0);
  const hasTransportation = salesData.some(entry => entry.transportation?.length > 0);
  const hasLoading = salesData.some(entry => entry.loading?.length > 0);

  const showSales = hasSales;
  const showTransportation = hasTransportation;
  const showLoading = hasLoading;
  const hasPurchase = purchaseData.some(entry => entry.sales?.length > 0);
  const hasPurchaseTransportation = purchaseData.some(entry => entry.transportation?.length > 0);
  const hasPurchaseLoading = purchaseData.some(entry => entry.loading?.length > 0);

  const showPurchase = hasPurchase;
  const showPurchaseTransportation = hasPurchaseTransportation;
  const showPurchaseLoading = hasPurchaseLoading;

  //   const handleEditLedger = useCallback((entry) => {

  //     const paymentFilter =
  //       entry?.payments?.length > 0 ? entry.payments[0].filter_table : null;

  //     const receiptFilter =
  //       entry?.receipts?.length > 0 ? entry.receipts[0].filter_table : null;

  //     const purchaseFilter =
  //       entry?.purchases?.length > 0 ? entry.purchases[0].filter_table : null;

  //     const salesFilter =
  //       entry?.sales?.length > 0 ? entry.sales[0].filter_table : null;

  //     const journalFilter =
  //       entry?.sales?.length > 0 ? entry.sales[0].filter_table : null;

  //     let voucher_type = null;

  //     if (paymentFilter === 'payment') voucher_type = 'payment';
  //     else if (receiptFilter === 'receipt') voucher_type = 'receipt';
  //     else if (purchaseFilter === 'purchase') voucher_type = 'purchase';
  //     else if (salesFilter === 'sales') voucher_type = 'sales';
  //     else if (
  //       journalFilter === 'from-journal' ||
  //       journalFilter === 'to-journal'
  //     ) voucher_type = 'from journal';

  //     const entryWithKey = {
  //       ...entry,

  //       payment_key: voucher_type === 'payment' ? entry.paymentKey : undefined,
  //       receipt_key: voucher_type === 'receipt' ? entry.receiptKey : undefined,
  //       purchase_key: voucher_type === 'purchase' ? entry.purchaseKey : undefined,
  //       entry_key: voucher_type === 'sales' ? entry.entryKey : undefined,
  //       journal_key: voucher_type === 'from journal' ? entry.purchaseKey : undefined,
  //       voucher_type,
  //       branchId,
  //     };

  //     setSelectedEntry(entryWithKey);
  //     setIsEditModalOpen(true);
  //   }, [branchId]);



  const handleModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedEntry(null);
  }, []);

  //   const handleUpdateSuccess = useCallback(() => {
  //     // fetchLedgerData(); // Refresh the ledger data
  //     handleModalClose();
  //     setRefreshKey(prev => prev + 1);
  //     if (branchId) {
  //       fetchPurchaseData();
  //       fetchSalesData();
  //       fetchPaymentData();
  //       fetchReceiptData();
  //       fetchJournalData();
  //     }
  //     // toast.success('Transaction updated successfully!');
  //   }, [branchId, fetchPurchaseData, fetchSalesData, fetchPaymentData, fetchReceiptData, fetchJournalData, handleModalClose]);

  //   const handleModalDateChange = (range) => {
  //     setStartDate(range.startDate);
  //     setEndDate(range.endDate);

  //     setDefaultDateRange({
  //       startDate: range.startDate,
  //       endDate: range.endDate,
  //     });
  //   };
  const handleRecoverEntryApi = async (entry) => {
    // console.log("Delete entry:", entry);

    try {
      let table_filter = null;

      if (entry?.sales?.length) table_filter = entry.sales[0].filter_table;
      else if (entry?.purchases?.length) table_filter = entry.purchases[0].filter_table;
      else if (entry?.receipts?.length) table_filter = entry.receipts[0].filter_table;
      else if (entry?.payments?.length) table_filter = entry.payments[0].filter_table;
      else if (entry?.journal?.length) table_filter = entry.journal[0].filter_table;

      if (!table_filter) throw new Error("Unable to recover entry type");

      let url = "";
      let payload = {
        branchId,
        entry,
      };

      switch (table_filter) {
        case "sales":
          url = "sales/recycle-sales-entry";
          payload.entryKey = entry.entryKey;
          break;

        case "purchase":
          url = "purchase/recycle-purchase-entry";
          payload.purchaseKey = entry.purchaseKey;
          break;

        case "receipt":
          url = "receipt/recycle-receipt-entry";
          payload.receiptKey = entry.receiptKey;
          break;

        case "payment":
          url = "payment/recycle-payment-entry";
          payload.paymentKey = entry.paymentKey;
          break;

        case "from-journal":
        case "to-journal":
          url = "journal/recycle-journal-entry";
          payload.journalKey = entry.purchaseKey;
          break;

        default:
          throw new Error("Invalid recover type");
      }



      const res = await api.post(url, payload);

      if (res.data.success) {
        toast.success("Entry recover successfully");
        setShowDeleteModal(false);
        setSelectedDeleteEntry(null);
        fetchPurchaseData();
        fetchSalesData();
        fetchPaymentData();
        fetchReceiptData();
        fetchJournalData();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || error.message || "recover failed");
    }
  };

  return (

    <div>
      <div className='sticky top-0 z-[990] pb-[0.5px]  bg-theme-webBackground'>
        {/* <Navbar dateFilterSingle={true} defaultDateRange={defaultDateRange} checkDate={true} heading={t("recycleBin")} /> */}
      </div>
      <div className=" p-0">
        {isDateLoading && (
          <div className="flex justify-center items-center w-full py-10">
            <div className="flex justify-center my-4">
              <LoadingSpinner size={70} color='#be8024' />
            </div>
          </div>
        )}
        {((!loading && receiptData && receiptData.length > 0) || (!loading && paymentData && paymentData.length > 0)) && (
          <div className='flex gap-2 w-full h-full '>
            <div className=" w-1/2 py-2  ">
              <div className='flex flex-col justify-between h-100'>
                <div className="overflow-x-auto lg:overflow-visible">
                  <table className="w-full table-auto border-separate border-spacing-0 ">
                    <thead>
                      <tr className="bg-theme-headerBg text-theme-color">
                        <th colSpan={5} className="border-b text-center py-[2px] font-medium text-sm">
                          {t("receipt")}
                        </th>
                        <th colSpan={3} className="border-b border-s  text-center py-[2px] font-medium text-sm">
                          {t("deletedBy")}
                        </th>
                      </tr>
                      <tr className="text-theme-headerBg bg-theme-color border-b border-theme-headerBg">
                        <th className="px-1 text-center font-medium border-b border-e text-sm">
                          {t("srNumber")}
                        </th>
                        <th className="px-1 p-[2px] font-medium border-b text-sm">
                          {t("receiptInfo")}
                        </th>
                        <th className="px-1 font-medium text-sm border-b">
                          {t("description")}
                        </th>
                        <th className="px-1 text-center font-medium text-sm border-b">
                          {t("paymentType")}
                        </th>
                        <th className="px-1 text-center font-medium text-sm border-b">
                          {t("amount")}
                        </th>
                        <th className="px-1 text-start border-s font-medium text-sm border-b">
                          {t("name")}
                        </th>
                        <th className="px-1  text-center font-medium text-sm border-b">
                          {t("reason")}
                        </th>
                        <th className="px-1 text-center font-medium border-b text-sm">
                          {t("action")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingTable === "receipt" ? (
                        <tr>
                          <td colSpan={8} className="py-6 text-center">
                            <LoadingSpinner size={40} color="#be8024" />
                          </td>
                        </tr>
                      ) : receiptData.length > 0 ? (
                        receiptData.map((entry, idx) => (
                          <tr key={entry.receiptKey} className="align-center bg-theme-tableBg">

                            <td className="border-b border-e text-center font-bold text-sm">
                              {(currentPage - 1) * itemsPerPage + idx + 1}
                            </td>

                            <td className="border-b break-words text-sm cursor-pointer">
                              {entry.receipts.map((row, i) => (
                                <div
                                  className="text-center"
                                  key={i}
                                  onClick={() =>
                                    navigate("/account-books/ledger-details", {
                                      state: {
                                        receiptRegister: entry.receipts[0],
                                        comeBack: "/daily-reports",
                                      },
                                    })
                                  }
                                >
                                  <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                    <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                                  </div>
                                  <div className="flex gap-2 justify-center w-32 capitalize">
                                    <ContactMarquee
                                      firstName={row.ledgerSubGroup}
                                      secondName={row.subGroupNameInHindi}
                                    />
                                  </div>
                                  <div className="w-32 capitalize">
                                    <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                                  </div>
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>

                            <td className="border-b px-1 text-sm">
                              {entry.receipts.map((row, i) => (
                                <div key={i}>
                                  {row.description}
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>

                            <td className="border-b text-center text-sm">
                              {entry.receipts.map((row, i) => (
                                <div key={i}>
                                  {row.paymentType ? row.paymentType.name : "-"}
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>

                            <td className="border-b text-center  text-sm">
                              {entry.receipts.map((row, i) => (
                                <div key={i}>
                                  {Number(row.amount).toLocaleString("en-US", {
                                    maximumFractionDigits: 0,
                                  })}
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b border-s  text-center text-sm fw-medium">
                              {entry.receipts.map((row, i) => (
                                <div key={i}>
                                  {row.creatorFirstName} {row.creatorLastName}
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b   text-center text-sm max-w-32">
                              {entry.receipts.map((row, i) => (
                                <div key={i}>
                                  {row.delete_reason}
                                  {i < entry.receipts.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>

                            <td className="border-b text-center align-middle">
                              <div className="flex justify-center gap-2">
                                <SlReload
                                  className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                                  onClick={() => {
                                    setSelectedDeleteEntry(entry);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Edit ledger"
                                />
                                {/* <RiDeleteBin6Fill
                                  className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                                  title="Delete ledger"
                                  onClick={() => {
                                    setSelectedDeleteEntry(entry);
                                    setShowDeleteModal(true);
                                  }}
                                /> */}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={8}
                            className="border-b bg-theme-tableBg py-2 text-sm text-center text-gray-500"
                          >
                            {t("noDataAvailable")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ================= PAGINATION (ONLY RECEIPT TABLE) ================= */}
                {(!loadingTable && receiptData && receiptData.length > 0 && <div className='mt-auto'>
                  <Pagination
                    data={{
                      allData: receiptData,
                      pagination: { totalRecords: receiptTotal },
                    }}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => {
                      handlePageChange(page, "receipt");
                      fetchReceiptData("pagination");
                    }}
                    onItemsPerPageChange={(items) => handleItemsPerPageChange(items, 'rececipt')}
                    position="bottom-center"
                    defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color rounded-full p-3 py-2 sm:p-4 sm:py-3 w-fit mx-auto mt-2"
                  />
                </div>)}

              </div>

            </div>
            <div className="w-1/2 py-2">

              <div className='flex flex-col justify-between h-100'>

                <div className="overflow-x-auto lg:overflow-visible">
                  <table className="w-full table-auto  border-separate border-spacing-0 overflow-hidden">
                    <thead>
                      <tr className="bg-theme-headerBg text-theme-tableBg">

                        <th colSpan={5} className="border-b py-[2px] text-center font-medium text-sm">{t('payment')}</th>
                        <th colSpan={3} className="border-b  border-s text-center py-[2px] font-medium text-sm">
                          {t("deletedBy")}
                        </th>

                      </tr>
                      <tr className="text-theme-headerBg border-theme-headerBg bg-theme-color">
                        <th className="border-b border-e px-1 text-center font-medium text-sm">{t("srNumber")}</th>
                        <th className="border-b px-1 py-[2px]  font-medium text-sm">{t('paymentInfo')}</th>
                        <th className="border-b px-1  font-medium text-sm">{t('description')}</th>
                        <th className="border-b px-1 text-center font-medium text-sm">{t('paymentType')}</th>
                        <th className="px-1 text-center  font-medium text-sm border-b">
                          {t("amount")}
                        </th>
                        <th className="px-1 text-start  border-s font-medium text-sm border-b">
                          {t("name")}
                        </th>
                        <th className="px-1  text-center font-medium text-sm border-b">
                          {t("reason")}
                        </th>
                        <th className="px-1 text-center font-medium border-b text-sm">
                          {t("action")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingTable === "payment" ? (
                        <tr>
                          <td colSpan={8} className="py-6 text-center">
                            <LoadingSpinner size={40} color="#be8024" />
                          </td>
                        </tr>
                      ) : paymentData.length > 0 ? (
                        paymentData && paymentData.map((entry, idx) => (
                          <tr key={entry.paymentKey} className="align-center bg-theme-tableBg">


                            <td className="border-b border-e text-center font-bold align-middle text-sm">

                              {(paymentCurrentPage - 1) * paymentItemsPerPage + idx + 1}


                            </td>


                            <td className="border-b  break-words text-sm cursor-pointer" title='check account details '>
                              {entry.payments.map((row, i) => (
                                <div className='text-center' key={i} onClick={() => {
                                  navigate('/account-books/ledger-details', {
                                    state: { paymentRegister: entry.payments[0], comeBack: "/daily-reports" }
                                  })
                                }}>
                                  <div className='flex gap-2 justify-center w-32 capitalize fw-medium'>

                                    <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                                  </div>
                                  <div className='flex gap-2 w-32 justify-center text-sm capitalize'>

                                    <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                                  </div>

                                  <div className="w-32 capitalize">
                                    <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                                  </div>
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b px-1 text-sm">
                              {entry.payments.map((row, i) => (
                                <div className='' key={i}>
                                  {row.description}
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b text-center text-sm">
                              {entry.payments.map((row, i) => (
                                <div className='' key={i}>
                                  {row.paymentType ? row.paymentType.name : '-'}
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>

                            <td className="border-b   text-center text-sm">
                              {entry.payments.map((row, i) => (
                                <div key={i}>
                                  {Number(row.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b  border-s text-center text-sm fw-medium">
                              {entry.payments.map((row, i) => (
                                <div key={i}>
                                  {row.creatorFirstName} {row.creatorLastName}
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b  text-center text-sm max-w-32">
                              {entry.payments.map((row, i) => (
                                <div key={i}>
                                  {row.delete_reason}
                                  {i < entry.payments.length - 1 && <hr />}
                                </div>
                              ))}
                            </td>
                            <td className="border-b   text-center align-middle">
                              <div className="flex justify-center gap-2">
                                <SlReload
                                  className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"

                                  onClick={() => {
                                    setSelectedDeleteEntry(entry);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Edit ledger"
                                />
                                {/* <RiDeleteBin6Fill
                                  className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                                  title="Delete ledger"
                                  onClick={() => {
                                    setSelectedDeleteEntry(entry);
                                    setShowDeleteModal(true);
                                  }}
                                /> */}
                              </div>
                            </td>

                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="border-b bg-theme-tableBg text-sm px-4 py-2 text-center text-gray-500 ">
                            {t('noDataAvailable')}
                          </td>
                        </tr>
                      )}
                    </tbody>

                  </table>
                </div>
                {(!loadingTable && paymentData && paymentData.length > 0 &&
                  <div className='mt-auto'>
                    <Pagination
                      data={{
                        allData: paymentData,
                        pagination: { totalRecords: paymentTotal },
                      }}
                      currentPage={paymentCurrentPage}
                      itemsPerPage={paymentItemsPerPage}
                      onPageChange={(page) => {
                        handlePageChange(page, "payment");
                        fetchPaymentData("pagination");
                      }}

                      onItemsPerPageChange={(items) => handleItemsPerPageChange(items, 'payment')}
                      position="bottom-center"
                      defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color rounded-full p-3 py-2 sm:p-4 sm:py-3 w-fit mx-auto mt-2"
                    />
                  </div>)}
              </div>

            </div>
          </div>)}
        {!loading && salesData && salesData.length > 0 && (
          <div className="w-full py-2">

            <div className='flex flex-col justify-between h-100'>


              <div className="overflow-x-auto lg:overflow-visible">
                <table className="w-full table-auto border-collapse overflow-hidden">
                  <thead>
                    <tr className="bg-theme-headerBg text-theme-color">

                      <th colSpan={(showTransportation || showLoading) ? "4" : "4"} className="border-e  py-[2px] text-center font-medium text-sm">
                        {t('sales')}
                      </th>

                      {showTransportation && (
                        <th colSpan={showTransportation && !showLoading ? "4" : "3"} className="  border-e  text-center font-medium text-sm">
                          {t('transportation')}
                        </th>
                      )}

                      {showLoading && (
                        <th colSpan={4} className=" text-center font-medium text-sm">
                          {t('loading/unloading')}
                        </th>
                      )}

                      <th colSpan={3} className=" text-center font-medium text-sm">
                          {t('deletedBy')}
                        </th>

                    </tr>
                    <tr className="text-theme-headerBg bg-white border-b border-theme-headerBg">
                      <th className="border-b border-e px-1 text-center font-medium text-sm">{t("srNumber")}</th>
                      <th className=" px-1 border-b  text-start font-medium text-sm w-32">
                        {t('customerInfo')}
                      </th>

                      <th className=" px-1 border-b  text-center font-medium text-sm">
                        {t('qty')}  {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                      </th>

                      <th className=" px-1 border-b text-center border-e font-medium text-sm">
                        {t('received')} ({t('paymentType')})
                      </th>


                      {showTransportation && (
                        <>
                          <th className=" px-1 border-b text-start font-medium text-sm w-32">
                            {t('vehicle')}
                          </th>

                          <th className=" px-1 border-b  text-center font-medium text-sm">
                            {t('qty')}  {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                          </th>

                          <th className=" px-1 border-b border-e text-center font-medium text-sm">
                            {t('paid')} ({t('paymentType')})
                          </th>

                        </>
                      )}

                      {showLoading && (
                        <>
                          <th className=" px-1 border-b  text-start font-medium text-sm w-32">
                            {t('name')}
                          </th>

                          <th className=" px-1  border-b text-center font-medium text-sm">
                            {t('qty')}  {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                          </th>

                          <th className=" px-1 border-b border-e text-center font-medium text-sm">
                            {t('paid')} ({t('paymentType')})
                          </th>
                        </>
                      )}
                       <th className=" px-1 border-b  text-center font-medium text-sm">
                        {t('name')}
                      </th>
                      <th className=" px-1 border-b   text-center font-medium text-sm">
                        {t('resason')}
                      </th>
                      <th className="border-b  px-1 text-center font-medium text-sm">
                        {t('actions')}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loadingTable === "sales" ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-center">
                          <LoadingSpinner size={40} color="#be8024" />
                        </td>
                      </tr>
                    ) : salesData.length > 0 ? (
                      salesData.map((entry, idx) => {
                        const maxRows = Math.max(
                          entry.sales.length,
                          entry.transportation?.length || 0,
                          entry.loading?.length || 0,
                          1
                        );

                        return [...Array(maxRows)].map((_, rowIndex) => {
                          const salesRow = entry.sales[rowIndex];
                          const transportRow = entry.transportation?.[rowIndex];
                          const loadingRow = entry.loading?.[rowIndex];

                          return (
                            <tr key={`${entry.entryKey}-${rowIndex}`} className="bg-theme-tableBg">
                              {/* Sr No */}
                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="border-b border-e text-center font-bold align-middle text-sm"
                                >

                                  {(salesCurrentPage - 1) * salesItemsPerPage + idx + 1}


                                </td>
                              )}

                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="break-words text-sm border-b border-s border-gray-200"
                                >
                                  {entry.sales.length > 0 && (
                                    <div className="text-sm flex flex-col">
                                      <div
                                        className="text-center p-0 cursor-pointer capitalize"
                                        title="Check account details"
                                        onClick={() => {
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              salesRegister: entry.sales[0],
                                              comeBack: "/daily-reports",
                                            },
                                          });
                                        }}
                                      >
                                        <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                          <ContactMarquee
                                            firstName={entry.sales[0].customer_name}
                                            secondName={entry.sales[0].cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 capitalize">
                                          <ContactMarquee
                                            firstName={entry.sales[0].ledgerSubGroup}
                                            secondName={entry.sales[0].subGroupNameInHindi}
                                          />
                                        </div>
                                      </div>
                                      <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar cursor-pointer">
                                        <ContactMarquee
                                          phoneVal={entry.sales[0].phone}
                                          addressVal={entry.sales[0].address}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </td>
                              )}

                              {/* Sales Qty */}
                              <td className="border-b text-center text-sm p-0">
                                {salesRow && (
                                  <>
                                    {Number(salesRow.quantity).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    {salesRow.unit}{" "}
                                    {salesRow.typeOfBricks ? salesRow.typeOfBricks.name : ""} @{" "}
                                    {Number(salesRow.rate).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    {salesRow.rate && "/"}
                                    {Number(salesRow.per_quantity).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    ={" "}
                                    {Number(salesRow.total).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </>
                                )}
                              </td>

                              {/* Received */}
                              <td className="border-b border-e  text-center text-sm p-0">
                                {salesRow && (
                                  <>
                                    {Number(salesRow.received).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    {salesRow.received > 0 &&
                                      salesRow.paymentType &&
                                      `(${salesRow.paymentType.name})`}
                                  </>
                                )}
                              </td>
                             

                              {/* Transportation */}
                              {showTransportation && (
                                <>
                                  <td
                                    className="border-b text-center text-sm cursor-pointer"
                                    title="Check account details"
                                  >
                                    {transportRow && (
                                      <div
                                        onClick={() =>
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              salesRegister: transportRow,
                                              comeBack: "/daily-reports",
                                            },
                                          })
                                        }
                                      >
                                        <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                          <ContactMarquee
                                            firstName={transportRow.name}
                                            secondName={transportRow.cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 px-1 capitalize">
                                          <ContactMarquee
                                            firstName={transportRow.ledgerSubGroup}
                                            secondName={transportRow.subGroupNameInHindi}
                                          />
                                        </div>
                                        <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                                          <ContactMarquee
                                            phoneVal={transportRow.phone}
                                            addressVal={transportRow.address}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>

                                  <td className="border-b text-center text-sm">
                                    {transportRow && (
                                      <>
                                        {Number(transportRow.quantity).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {transportRow.unit}{" "}
                                        {transportRow.typeOfBricks
                                          ? transportRow.typeOfBricks.name
                                          : ""}{" "}
                                        @{" "}
                                        {Number(transportRow.rate).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {transportRow.rate && "/"}
                                        {Number(transportRow.per_quantity).toLocaleString(
                                          "en-US",
                                          { maximumFractionDigits: 0 }
                                        )}{" "}
                                        ={" "}
                                        {Number(transportRow.total).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                      </>
                                    )}
                                  </td>

                                  <td className="border-b border-e text-center text-sm">
                                    {transportRow && (
                                      <>
                                        {Number(transportRow.paid).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {transportRow.paid > 0 &&
                                          transportRow.paymentType &&
                                          `(${transportRow.paymentType.name})`}
                                      </>
                                    )}
                                  </td>
                                </>
                              )}

                              {/* Loading */}
                              {showLoading && (
                                <>
                                  <td
                                    className="border-b text-center text-sm cursor-pointer"
                                    title="Check account details"
                                  >
                                    {loadingRow && (
                                      <div
                                        onClick={() =>
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              salesRegister: loadingRow,
                                              comeBack: "/daily-reports",
                                            },
                                          })
                                        }
                                      >
                                        <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                          <ContactMarquee
                                            firstName={loadingRow.name}
                                            secondName={loadingRow.cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 capitalize">
                                          <ContactMarquee
                                            firstName={loadingRow.ledgerSubGroup}
                                            secondName={loadingRow.subGroupNameInHindi}
                                          />
                                        </div>
                                        <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                                          <ContactMarquee
                                            phoneVal={loadingRow.phone}
                                            addressVal={loadingRow.address}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>

                                  <td className="border-b text-center text-sm">
                                    {loadingRow && (
                                      <>
                                        {Number(loadingRow.quantity).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {loadingRow.unit}{" "}
                                        {loadingRow.typeOfBricks
                                          ? loadingRow.typeOfBricks.name
                                          : ""}{" "}
                                        @{" "}
                                        {Number(loadingRow.rate).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {loadingRow.rate && "/"}
                                        {Number(loadingRow.per_quantity).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        ={" "}
                                        {Number(loadingRow.total).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                      </>
                                    )}
                                  </td>

                                  <td className="border-b border-e text-center text-sm">
                                    {loadingRow && (
                                      <>
                                        {Number(loadingRow.paid).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {loadingRow.paid > 0 &&
                                          loadingRow.paymentType &&
                                          `(${loadingRow.paymentType.name})`}
                                      </>
                                    )}
                                  </td>
                                </>
                              )}
                                <td className="border-b  text-center text-sm p-0 fw-medium">
                                {salesRow && (
                                  <>

                                    {salesRow.creatorFirstName} {salesRow.creatorLastName}
                                  </>
                                )}
                              </td>
                              <td className="border-b  text-center text-sm p-0 max-w-32">
                                {salesRow && (
                                  <>
                                    {salesRow.delete_reason}
                                  </>
                                )}
                              </td>

                              {/* Actions */}
                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="border-b text-center align-middle"
                                >
                                  <div className="flex justify-center gap-2">
                                    <SlReload
                                      className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                                      onClick={() => {
                                        setSelectedDeleteEntry(entry);
                                        setShowDeleteModal(true);
                                      }}
                                      title="Edit ledger"
                                    />
                                    {/* <RiDeleteBin6Fill
                                      className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                                      title="Delete ledger"
                                      onClick={() => {
                                        setSelectedDeleteEntry(entry);
                                        setShowDeleteModal(true);
                                      }}
                                    /> */}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        });
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="13"
                          className="border-b px-4 bg-theme-tableBg py-2 text-sm text-center text-gray-500"
                        >
                          {t("noDataAvailable")}
                        </td>
                      </tr>
                    )}
                  </tbody>

                </table>
              </div>
              {(!loadingTable && salesData && salesData.length > 0 && <div className='mt-auto'>
                <Pagination
                  data={{
                    allData: salesData,
                    pagination: { totalRecords: salesTotal },
                  }}
                  currentPage={salesCurrentPage}
                  itemsPerPage={salesItemsPerPage}
                  onPageChange={(page) => {
                    handlePageChange(page, "sales");
                    fetchReceiptData("pagination");
                  }}
                  onItemsPerPageChange={(items) => handleItemsPerPageChange(items, 'sales')}
                  position="bottom-center"
                  defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color rounded-full p-3 py-2 sm:p-4 sm:py-3 w-fit mx-auto mt-2"
                />
              </div>)}
            </div>

          </div>)}
        {!loading && purchaseData && purchaseData.length > 0 && (
          <div className="w-full py-2">

            <div className='flex flex-col justify-between h-100'>

              <div className="overflow-x-auto lg:overflow-visible">
                <table className="w-full table-auto border-collapse overflow-hidden">
                  <thead>
                    <tr className="bg-theme-headerBg text-theme-color">

                      <th colSpan={(showPurchaseTransportation || showPurchaseLoading) ? "4" : "4"} className="border-e py-[2px]  text-center font-medium text-sm">{t('purchase')}</th>
                      {showPurchaseTransportation &&
                        <th colSpan={showPurchaseTransportation && !showPurchaseLoading ? "4" : "3"} className="border-e py-[2px]  text-center font-medium text-sm">{t('transportation')}</th>}
                      {showPurchaseLoading &&
                        <th colSpan={4} className="  py-[2px] text-center font-medium text-sm">{t('loading/unloading')}</th>}
                        <th colSpan={3} className="  py-[2px] text-center font-medium text-sm">{t('deletedBy')}</th>
                    </tr>
                    <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                      <th className="border-e border-b  px-1 text-center font-medium text-sm">{t("srNumber")}</th>
                      <th className="border-b px-1 text-start font-medium text-sm w-32">{t('vendorInfo')}</th>
                      <th className="border-b px-1 text-center font-medium text-sm">
                        {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                      </th>
                      <th className="border-b  px-1 text-center border-e font-medium text-sm">{t('paid')} ({t('paymentType')})</th>
                  
                      {showPurchaseTransportation && <>
                        <th className="border-b px-1 text-start font-medium text-sm w-32">{t('vehicle')}</th>
                        <th className="border-b px-1 text-center font-medium text-sm">
                          {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                        </th>
                        <th className="border-b border-e px-1 text-center font-medium text-sm">{t('paid')} ({t('paymentType')})</th>
                      </>}
                      {showPurchaseLoading && <>
                        <th className="border-b px-1 text-start font-medium text-sm w-32">{t('name')}</th>
                        <th className="border-b px-1 text-center font-medium text-sm">
                          {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                        </th>
                        <th className="border-b border-e px-1 text-center font-medium text-sm">{t('paid')} ({t('paymentType')})</th>
                      </>}
                        <th className="border-b px-1 text-center  font-medium text-sm"> {t('name')}</th>
                      <th className="border-b  px-1 text-center font-medium text-sm">{t('reason')}</th>
                      <th className="border-b  px-1 text-center font-medium text-sm">{t('actions')}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loadingTable === "purchase" ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-center">
                          <LoadingSpinner size={40} color="#be8024" />
                        </td>
                      </tr>
                    ) : purchaseData.length > 0 ? (
                      purchaseData.map((entry, idx) => {
                        const maxRows = Math.max(
                          entry.purchases.length || 1,
                          entry.transportation?.length || 1,
                          entry.loading?.length || 1
                        );

                        return [...Array(maxRows)].map((_, rowIndex) => {
                          const sale = entry.purchases[rowIndex];
                          const transport = entry.transportation?.[rowIndex];
                          const load = entry.loading?.[rowIndex];

                          return (
                            <tr key={`${entry.purchaseKey}-${rowIndex}`} className="bg-theme-tableBg">
                              {/* Sr No */}
                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="border-b border-e text-center font-bold align-middle text-sm"
                                  title="Check account details"
                                >

                                  {(purchaseCurrentPage - 1) * purchaseItemsPerPage + idx + 1}


                                </td>
                              )}

                              {/* Vendor Info */}
                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="border-b break-words text-sm cursor-pointer"
                                >
                                  {entry.purchases.length > 0 && (
                                    <div className="text-sm flex flex-col">
                                      <div
                                        className="text-center p-0 cursor-pointer capitalize"
                                        title="Check account details"
                                        onClick={() => {
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              purchaseRegister: entry.purchases[0],
                                              comeBack: "/daily-reports",
                                            },
                                          });
                                        }}
                                      >
                                        <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                                          <ContactMarquee
                                            firstName={entry.purchases[0].vendor}
                                            secondName={entry.purchases[0].cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 capitalize">
                                          <ContactMarquee
                                            firstName={entry.purchases[0].ledgerSubGroup}
                                            secondName={entry.purchases[0].subGroupNameInHindi}
                                          />
                                        </div>
                                      </div>
                                      <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar cursor-pointer">
                                        <ContactMarquee
                                          phoneVal={entry.purchases[0].phone}
                                          addressVal={entry.purchases[0].address}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </td>
                              )}

                              {/* Purchase Qty */}
                              <td className="border-b text-center text-sm px-1">
                                {sale && (
                                  <>
                                    {Number(sale.quantity).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    {sale.unit} {sale.items?.name || ""} @{" "}
                                    {Number(sale.rate).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                    {sale.rate && "/"}
                                    {Number(
                                      sale.perQuantity === 0 ? sale.perQuantity : 1
                                    ).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                                    ={" "}
                                    {Number(sale.total).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </>
                                )}
                              </td>

                              {/* Paid */}
                              <td className="border-b text-center border-e text-sm px-1 ">
                                {sale && (
                                  <>
                                    {Number(sale.paid).toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    {sale.paid > 0 &&
                                      sale.paymentType &&
                                      `(${sale.paymentType.name})`}
                                  </>
                                )}
                              </td>
                             

                              {/* Transportation */}
                              {showPurchaseTransportation && (
                                <>
                                  {/* Vehicle */}
                                  <td
                                    className="border-b text-center text-sm px-[2px] cursor-pointer"
                                    title="Check account details"
                                  >
                                    {transport && (
                                      <div
                                        onClick={() => {
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              purchaseRegister: transport,
                                              comeBack: "/daily-reports",
                                            },
                                          });
                                        }}
                                      >
                                        <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                          <ContactMarquee
                                            firstName={transport.name}
                                            secondName={transport.cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 capitalize">
                                          <ContactMarquee
                                            firstName={transport.ledgerSubGroup}
                                            secondName={transport.subGroupNameInHindi}
                                          />
                                        </div>
                                        <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar cursor-pointer">
                                          <ContactMarquee
                                            phoneVal={transport.phone}
                                            addressVal={transport.address}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>

                                  {/* Transport Qty */}
                                  <td className="border-b text-center text-sm px-1">
                                    {transport && (
                                      <>
                                        {Number(transport.quantity).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {transport.unit} {transport.items?.name || ""} @{" "}
                                        {Number(transport.rate).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                        {transport.rate && "/"}
                                        {Number(
                                          transport.perQuantity === 0
                                            ? transport.perQuantity
                                            : 1
                                        ).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        ={" "}
                                        {Number(transport.total).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                      </>
                                    )}
                                  </td>

                                  {/* Transport Paid */}
                                  <td className="border-b border-e text-center text-sm px-1">
                                    {transport && (
                                      <>
                                        {Number(transport.paid).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {transport.paid > 0 &&
                                          transport.paymentType &&
                                          `(${transport.paymentType.name})`}
                                      </>
                                    )}
                                  </td>
                                </>
                              )}

                              {/* Loading */}
                              {showPurchaseLoading && (
                                <>
                                  {/* Name */}
                                  <td
                                    className="border-b text-center text-sm cursor-pointer"
                                    title="Check account details"
                                  >
                                    {load && (
                                      <div
                                        onClick={() => {
                                          navigate("/account-books/ledger-details", {
                                            state: {
                                              purchaseRegister: load,
                                              comeBack: "/daily-reports",
                                            },
                                          });
                                        }}
                                      >
                                        <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                                          <ContactMarquee
                                            firstName={load.name}
                                            secondName={load.cousinName}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-center w-32 capitalize">
                                          <ContactMarquee
                                            firstName={load.ledgerSubGroup}
                                            secondName={load.subGroupNameInHindi}
                                          />
                                        </div>
                                        <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar cursor-pointer">
                                          <ContactMarquee
                                            phoneVal={load.phone}
                                            addressVal={load.address}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>

                                  {/* Loading Qty */}
                                  <td className="border-b text-center text-sm px-1">
                                    {load && (
                                      <>
                                        {Number(load.quantity).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {load.unit} {load.items?.name || ""} @{" "}
                                        {Number(load.rate).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                        {load.rate && "/"}
                                        {Number(
                                          load.perQuantity === 0 ? load.perQuantity : 1
                                        ).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        ={" "}
                                        {Number(load.total).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}
                                      </>
                                    )}
                                  </td>

                                  {/* Loading Paid */}
                                  <td className="border-b border-e text-center text-sm px-1">
                                    {load && (
                                      <>
                                        {Number(load.paid).toLocaleString("en-US", {
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        {load.paid > 0 &&
                                          load.paymentType &&
                                          `(${load.paymentType.name})`}
                                      </>
                                    )}
                                  </td>
                                </>
                              )}
                               <td className="border-b text-center text-sm px-1 fw-medium ">
                                {sale && (
                                  <>
                                    {sale.creatorFirstName} {sale.creatorLastName}
                                  </>
                                )}
                              </td>
                              <td className="border-b text-center text-sm px-1 max-w-32">
                                {sale && (
                                  <>
                                    {sale.delete_reason}
                                  </>
                                )}
                              </td>

                              {/* Actions */}
                              {rowIndex === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="border-b text-center align-middle"
                                >
                                  <div className="flex justify-center gap-2">
                                    <SlReload
                                      className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                                      onClick={() => {
                                        setSelectedDeleteEntry(entry);
                                        setShowDeleteModal(true);
                                      }}
                                      title="recycle ledger"
                                    />
                                    {/* <RiDeleteBin6Fill
                                      className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                                      title="Delete ledger"
                                      onClick={() => {
                                        setSelectedDeleteEntry(entry);
                                        setShowDeleteModal(true);
                                      }}
                                    /> */}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        });
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="15"
                          className="border-b px-4 bg-theme-tableBg py-2 text-center text-gray-500 text-sm"
                        >
                          {t("noDataAvailable")}
                        </td>
                      </tr>
                    )}
                  </tbody>

                </table>
              </div>
              {(!loadingTable && purchaseData && purchaseData.length > 0 && <div className='mt-auto'>
                <Pagination
                  data={{
                    allData: purchaseData,
                    pagination: { totalRecords: purchaseTotal },
                  }}
                  currentPage={purchaseCurrentPage}
                  itemsPerPage={purchaseItemsPerPage}
                  onPageChange={(page) => {
                    handlePageChange(page, "purchase");
                    fetchReceiptData("pagination");
                  }}
                  onItemsPerPageChange={(items) => handleItemsPerPageChange(items, 'purchase')}
                  position="bottom-center"
                  defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color rounded-full p-3 py-2 sm:p-4 sm:py-3 w-fit mx-auto mt-2"
                />
              </div>)}
            </div>

          </div>
        )}
        {!loading && journalData && journalData.length > 0 && (
          <div className='flex flex-col justify-between h-100'>

            <div className="overflow-x-auto lg:overflow-visible my-2 pt-1">
              <table className="w-full table-auto border-separate border-spacing-0 overflow-hidden">
                <thead>
                  <tr className="bg-theme-headerBg text-theme-color ">
                    <th colSpan={3} className="border-e py-[2px]  text-center font-medium text-sm">{t('from')}</th>
                    <th colSpan={2} className=" py-[2px] border-e  text-center font-medium text-sm">{t('to')}</th>
                    <th colSpan={3} className=" py-[2px]  text-center font-medium text-sm">{t('deletedBy')}</th>
                  </tr>
                  <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                    <th className=" border-b border-e px-1 text-center font-medium text-sm">{t('srNumber')}</th>
                    <th className="border-b px-2  py-[2px] text-start font-medium text-sm max-w-32">{t('name')}</th>
                    <th className="border-b  border-e  text-start font-medium text-sm">
                      {t('qty')}  {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                    </th>
                    
                    <th className="border-b px-2  text-start font-medium text-sm max-w-32">{t('name')}</th>
                    <th className="border-b border-e px-1  text-start font-medium text-sm">
                      {t('qty')}  {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                    </th>
                    <th className="border-b px-1 text-start font-medium text-sm">
                      {t('name')}
                    </th>
                    <th className="border-b   text-start font-medium text-sm">
                      {t('reason')}
                    </th>
                    <th className="border-b  px-1 text-center font-medium text-sm">{t('actions')}</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingTable === "journal" ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center">
                        <LoadingSpinner size={40} color="#be8024" />
                      </td>
                    </tr>
                  ) : journalData && journalData.map((entry, idx) => (
                    <tr key={entry.purchaseKey} className="align-center bg-theme-tableBg">
                      <td className="border-b border-e text-center font-bold align-middle text-sm" title='Check account details'>

                        {(journalCurrentPage - 1) * journalItemsPerPage + idx + 1}
                      </td>

                      <td className="border-b  break-words px-1 text-sm cursor-pointer">
                        {entry.sales.map((row, i) => (
                          <div className="text-sm flex flex-col " key={i}>
                            <div className='text-center p-0  cursor-pointer ' title='Check account details'
                              onClick={() => {
                                navigate('/account-books/ledger-details', {
                                  state: { purchaseRegister: row, comeBack: "/daily-reports" }
                                })
                              }}
                            >
                              <div className="flex gap-2 justify-center w-44 capitalize fw-medium">

                                <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                              </div>
                              <div className="flex gap-2 justify-center w-44 capitalize">

                                <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                              </div>
                            </div>

                            <div className='p-0 text-gray-600 w-44  text-nowrap overflow-x-scroll custom-scrollbar cursor-pointer capitalize'>

                              <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                            </div>
                          </div>

                        ))}
                      </td>
                      <td className="border-b text-start border-e   text-sm p-0">
                        {entry.sales.map((row, i) => (
                          <div className=' m-0 p-0 ' key={i}>

                            {Number(row.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.unit} {row.items ? row.items.name : ''} @ {Number(row.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.rate && "/"}{Number((row.perQuantity === 0) ? row.perQuantity : "1").toLocaleString("en-US", { maximumFractionDigits: 0 })}  = {Number(row.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}<br />
                            {i < entry.sales.length - 1 && <hr className="my-1" />}
                          </div>
                        ))}
                      </td>
                    



                      <td className="border-b text-center text-sm px-[2px] cursor-pointer" title='Check account details'>
                        {entry.transportation.map((row, i) => (
                          <div className='' key={i} onClick={() => {
                            navigate('/account-books/ledger-details', {
                              state: { purchaseRegister: row, comeBack: "/daily-reports" }
                            })
                          }}>

                            <div className="flex gap-2 justify-center w-44 capitalize fw-medium">

                              <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-44 capitalize">

                              <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                            </div>
                            <div className='p-0 text-gray-600 w-44  text-nowrap overflow-x-scroll capitalize custom-scrollbar cursor-pointer'>

                              <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                            </div>
                            {i < entry.transportation.length - 1 && <hr className="my-1" />}

                          </div>
                        ))}
                      </td>

                      <td className="border-b border-e text-start px-1 text-sm p-0">
                        {entry.transportation.map((row, i) => (
                          <div key={i} className='m-0 p-0'>

                            {Number(row.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.unit} {row.items ? row.items.name : ''} @ {Number(row.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.rate && "/"}{Number((row.perQuantity === 0) ? row.perQuantity : "1").toLocaleString("en-US", { maximumFractionDigits: 0 })}  = {Number(row.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}<br />
                            {i < entry.transportation.length - 1 && <hr className=" my-1" />}
                          </div>
                        ))}
                      </td>

                        <td className="border-b text-start   text-sm p-0 fw-medium ">
                        {entry.sales.map((row, i) => (
                          <div className=' m-0 px-1 ' key={i}>
                            {row.creatorFirstName} {row.creatorLastName}
                          </div>
                        ))}
                      </td>
                      <td className="border-b text-start   text-sm p-0 max-w-32">
                        {entry.sales.map((row, i) => (
                          <div className=' m-0 p-0 ' key={i}>

                            {row.delete_reason}
                          </div>
                        ))}
                      </td>

                      <td className="border-b   text-center align-middle">
                        <div className="flex justify-center gap-2">
                          <SlReload
                            className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                            onClick={() => {
                              setSelectedDeleteEntry(entry);
                              setShowDeleteModal(true);
                            }}
                            title="Edit ledger"
                          />
                          {/* <RiDeleteBin6Fill
                            className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                        
                            title="Delete ledger"
                            onClick={() => {
                              setSelectedDeleteEntry(entry);
                              setShowDeleteModal(true);
                            }}
                          /> */}
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
            {(!loadingTable && journalData && journalData.length > 0 && <div className='mt-auto'>
              <Pagination
                data={{
                  allData: journalData,
                  pagination: { totalRecords: journalTotal },
                }}
                currentPage={journalCurrentPage}
                itemsPerPage={journalItemsPerPage}
                onPageChange={(page) => {
                  handlePageChange(page, "journal");
                  fetchReceiptData("pagination");
                }}
                onItemsPerPageChange={(items) => handleItemsPerPageChange(items, 'journal')}
                position="bottom-center"
                defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color rounded-full p-3 py-2 sm:p-4 sm:py-3 w-fit mx-auto mt-2"
              />
            </div>)}
          </div>
        )}

      </div>
      {receiptData && receiptData.length <= 0 && paymentData && paymentData.length <= 0 && salesData && salesData.length <= 0 && purchaseData && purchaseData.length <= 0 &&
        <div className=' py-5 text-sm  text-center text-gray-500 '>{t('noDataAvailable')}</div>}
      <div>

      </div>
      {showDeleteModal && (
        <RecoverDeleteDataModal
          selectedLedger={selectedDeleteEntry}
          handleModalClose={() => {
            setShowDeleteModal(false);
            setSelectedDeleteEntry(null);
          }}
          handleRecoverApi={handleRecoverEntryApi}
        />
      )}
    </div>
  )
}



